import React, { useContext, useEffect } from "react"
import styled from "@emotion/styled"
import Slider from "react-slick"
import { Flex } from "@rebass/grid/emotion"

import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"

import { ResultsContext } from "../resultscontext/resultscontext"

import ResultsCard from "../resultscard/resultscard"
import ResultsCardAlt from "../resultscard/resultscardalt"
import { NextArrow, PrevArrow } from "../resultsarrows/resultsarrows"

import { getArrowDirectionClicked } from "../../../../../utils/general"
import theme from "@nutrafol/nutrafol-ui-kit/styles/theme"

const SliderWrapper = styled.div`
  position: relative;
  margin: 0 -4px;
  width: calc(100% + 8px);

  .slick-next,
  .slick-prev {
    position: absolute;
    z-index: 9;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: 0;
    width: auto;
    height: auto;
    padding: 0 !important;
  }

  .slick-slider,
  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: 100% !important;
  }
`

const CardWrapper = styled.div`
  padding: 0 4px;
  height: 100%;

  @media (min-width: ${theme.mediaqueries.large}) {
    padding: 0 8px;
  }

  p {
    a {
      text-decoration: underline;
      text-underline-offset: 4px;
    }
  }
`

const NextArrowWrapper = styled.div`
  right: -36px !important;
  @media (min-width: 1280px) {
    right: -58px !important;
  }
`

const PrevArrowWrapper = styled.div`
  left: -36px !important;
  @media (min-width: 1280px) {
    left: -58px !important;
  }
`

const ResultsSlider = ({
  cardDirection,
  cardComponent = false,
  trackObject = {},
  settings = {},
  slides,
  badgePosition,
  showDisclaimer,
  showDisclaimerAlt,
  disclaimer,
}) => {
  let CardComponent = null

  if (cardComponent) {
    CardComponent = cardComponent
  } else if (cardDirection === "row") {
    CardComponent = ResultsCardAlt
  } else {
    CardComponent = ResultsCard
  }

  const {
    currentItemIndex,
    setCurrentItemIndex,
    autoplay,
    setAutoplay,
    autoplaySpeed,
    setAutoplaySpeed,
    resultSliderRef,
    setResultSliderRef,
    thumbnailSliderRef,
  } = useContext(ResultsContext)

  const track = (index, arrowDirection) => {
    segmentEvent("Carousel Clicked", {
      module_location: trackObject?.module_location || "mid_fold",
      module_name: "Before & After",
      slide_number: index + 1,
      arrow: arrowDirection,
    })
  }

  const handleUserInteraction = (dir) => {
    if (autoplay) {
      setAutoplay(false)
      track(currentItemIndex + 1, dir)
    }
  }

  const arrowClick = (dir) => {
    if (autoplay) {
      setAutoplay(false)
      track(currentItemIndex + 1, dir)
    }
  }

  const sliderSettings = {
    initialSlide: 0,
    asNavFor: thumbnailSliderRef,
    ref: (slider) => setResultSliderRef(slider),
    infinite: true,
    speed: 500,
    slidesToShow: cardDirection === "row" ? 1 : 3,
    slidesToScroll: 1,
    adaptiveHeight: false,
    dots: false,
    arrows: false,
    autoplaySpeed: autoplaySpeed,
    swipe: true,
    draggable: true,
    autoplay: autoplay || true,
    nextArrow: (
      <NextArrowWrapper>
        <NextArrow
          onClick={(e) => arrowClick("right")}
          width={[24, 24, 24, 24, 48]}
          height={[24, 24, 24, 24, 48]}
        />
      </NextArrowWrapper>
    ),
    prevArrow: (
      <PrevArrowWrapper>
        <PrevArrow
          onClick={(e) => arrowClick("left")}
          width={[24, 24, 24, 24, 48]}
          height={[24, 24, 24, 24, 48]}
        />
      </PrevArrowWrapper>
    ),
    responsive: [
      {
        breakpoint: 9999,
        settings: {
          arrows: cardDirection !== "row",
        },
      },
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 1,
          arrows: false,
        },
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1,
          arrows: false,
        },
      },
    ],
    beforeChange: (oldIndex, newIndex) => {
      setCurrentItemIndex(newIndex)
      if (!autoplay) {
        track(
          newIndex,
          getArrowDirectionClicked(oldIndex, newIndex, slides.length)
        )
      }
    },
    onSwipe(dir) {
      handleUserInteraction()
      setAutoplay(false)
      track(currentItemIndex + 1, dir === "left" ? "right" : "left")
    },
    ...settings,
  }

  useEffect(() => {
    /*
     * if (isMobile) {
     *   //setAutoplay(false)
     * }
     */
    if (settings.autoplay === false) {
      setAutoplay(false)
    }
    if (settings.autoplaySpeed) {
      setAutoplaySpeed(settings.autoplaySpeed)
    }
  }, [settings, setAutoplay, setAutoplaySpeed])

  useEffect(() => {
    if (!autoplay) {
      setTimeout(resultSliderRef?.slickPause)
    }
  }, [autoplay])

  return (
    <Flex>
      <SliderWrapper>
        <Slider {...sliderSettings}>
          {slides.map((slide, index) => {
            return (
              <CardWrapper key={`card-wrapper-${index}`}>
                <CardComponent
                  {...slide}
                  badgePosition={badgePosition}
                  disclaimer={disclaimer}
                  showDisclaimer={showDisclaimer}
                  showDisclaimerAlt={showDisclaimerAlt}
                />
              </CardWrapper>
            )
          })}
        </Slider>
      </SliderWrapper>
    </Flex>
  )
}

export default ResultsSlider
